import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, forwardRef, OnInit, ViewChild, ElementRef } from '@angular/core';
import { isObservable, Observable, of } from 'rxjs';
import { SelectModel } from '@shared/components/select/models/select';
import { ControlValueAccessor, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { map, startWith, switchMap } from 'rxjs/operators';
import { CustomIcons } from '@shared/models/custom-icons';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { AppSelectErrorStateMatcher } from '@utils/form-validators';
import { ErrorStateMatcher } from '@angular/material/core';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALIDATORS,
      // eslint-disable-next-line no-use-before-define
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    },
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line no-use-before-define
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    }
  ]
})
export class SelectComponent implements ControlValueAccessor, OnInit {
  private _value: string | number | boolean;
  private _options: Observable<SelectModel[]>;
  public maxDropdownHeight = 200;
  public optionHeight = 48;

  @ViewChild('inputAutoComplete') public inputAutoComplete: ElementRef;
  @ViewChild(MatAutocompleteTrigger) public autoComplete: MatAutocompleteTrigger;
  public icons = CustomIcons;

  public isOpen = false;

  @Input() public selectOptions: Observable<SelectModel[]> | SelectModel[] = [];
  public searchPhrase = new UntypedFormControl('');

  public get value(): string | number | boolean {
    return this._value;
  }

  @Input()
  public set value(value: string | number | boolean) {
    this._value = value;
    this.onChange(this._value);
    this.onTouch(this._value);
  }

  @Input() public required: boolean;
  @Input() public placeholder: string;
  @Input() public loading: boolean | undefined;
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() public readonly click = new EventEmitter();
  @Output() public readonly selectedValue = new EventEmitter<string | number | boolean | undefined>();

  public errorMatcher: ErrorStateMatcher;
  public filteredOptions: Observable<SelectModel[]>;
  private getOrMakeObservable(): Observable<SelectModel[]> {
    if (isObservable(this.selectOptions)) return this.selectOptions;
    return of(this.selectOptions);
  }

  private validate(c: UntypedFormControl) {
    if (!this.errorMatcher && c) {
      this.errorMatcher = new AppSelectErrorStateMatcher(c);
    }
  }
  private filter(value: string): Observable<SelectModel[]> {
    return this._options.pipe(map(options => options.filter(option => String(option.value).toLowerCase().includes(value.toLowerCase()))));
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: any = (): void => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouch: any = (): void => {};

  public ngOnInit(): void {
    if (!isObservable(this.selectOptions)) {
      const value = this.selectOptions.find(o => o.value === this.value);
      if (value) {
        this.searchPhrase.setValue(value.title, { emitEvent: false });
      }
    } else {
      this.searchPhrase.setValue(this.value, { emitEvent: false });
    }

    this._options = this.getOrMakeObservable();
    this.filteredOptions = (this.searchPhrase.valueChanges as Observable<string>).pipe(
      startWith(this.value ? this.value.toString() : ''),
      switchMap((value: string) => {
        if (this.value !== value) {
          this.selectedValue.emit('');
          this.value = '';
        }

        return this.filter(value);
      })
    );
  }

  public chooseOption(optionValue: string | number | boolean, title: string): void {
    this.selectedValue.emit(optionValue);
    this.value = optionValue;
    this.searchPhrase.setValue(title, { emitEvent: false });
  }

  public clearValue(): void {
    this.searchPhrase.setValue('');
    this.selectedValue.emit('');
    this.value = '';
  }

  public selectOpen(): void {
    this.onTouch(this._value);
    this.click.emit();
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public writeValue(value: string | number): void {
    this.value = value;
  }

  public openPanel($event: MouseEvent): void {
    $event.stopPropagation();
    this.inputAutoComplete.nativeElement.focus();
    this.autoComplete.openPanel();
  }

  public closePanel(): void {
    this.autoComplete?.closePanel();
    this.inputAutoComplete.nativeElement.blur();
  }
}
